// Energy-market palettes. Market BASE/named = identity (flex/forward split,
// bright cool); SERIES ramps fan WIDE across the cool spectrum for in-chart
// readability (series-1 ~= base). Keep in sync with theme.css :root.

export const marketLight = {
  forward: {
    base: '37 99 235',
    soft: '219 234 254',
    strong: '30 64 175',
    muted: '96 165 250',
    electricity: '37 99 235',
    gas: '139 92 246',
    coal: '236 72 153',
    oil: '16 185 129',
    emissions: '99 102 241',
    series: {
      1: '33 90 222',
      2: '78 21 142',
      3: '42 223 211',
      4: '21 119 142',
      5: '86 166 230',
      6: '21 57 142',
      7: '127 42 223',
      8: '169 113 234',
    },
  },
  dayAhead: {
    base: '16 185 129',
    soft: '209 250 229',
    strong: '6 95 70',
    muted: '52 211 153',
    // TB-spread indices TB1…TB12 (PI-255): ONE ordered ramp. TBn is nested
    // (TB1 <= … <= TB12), so colour order = legend order = vertical order.
    // Hue walks emerald → teal → petrol → navy (OKLCH 163° → 265°), the same hue
    // per index in both modes; lightness falls with n here. TB1 is emerald-600 in
    // both modes (the light brand primary-hover). Every step >= 3.4:1 on surface,
    // raised and subtle. Mirrors: theme.css :root and backend renderer/colors.py THEME_TOKENS.
    tb1: '5 150 105',
    tb2: '4 118 115',
    tb3: '0 102 114',
    tb4: '2 86 111',
    tb5: '1 78 108',
    tb6: '0 70 105',
    tb7: '0 62 103',
    tb8: '1 54 101',
    tb9: '0 47 98',
    tb10: '0 39 95',
    tb11: '0 30 95',
    tb12: '3 19 93',
    series: {
      1: '36 219 158',
      2: '23 140 101',
      3: '34 134 211',
      4: '34 211 99',
      5: '115 212 232',
      6: '34 211 196',
      7: '23 120 140',
      8: '115 181 232',
    },
  },
  intraday: {
    base: '139 92 246',
    soft: '237 233 254',
    strong: '91 33 182',
    muted: '167 139 250',
    ida1: '124 58 237',
    ida2: '139 92 246',
    ida3: '167 139 250',
    idc: '109 40 217',
    series: {
      1: '104 38 217',
      2: '129 116 231',
      3: '116 162 231',
      4: '139 24 123',
      5: '231 116 215',
      6: '196 47 218',
      7: '24 40 139',
      8: '24 70 139',
    },
  },
  reserve: {
    base: '37 99 235',
    soft: '219 234 254',
    strong: '30 64 175',
    muted: '96 165 250',
    fcr: '37 99 235',
    afrr: '59 130 246',
    afrrUp: '59 130 246',
    afrrDown: '30 64 175',
    mfrr: '96 165 250',
    mfrrUp: '96 165 250',
    mfrrDown: '30 58 138',
    series: {
      1: '31 105 224',
      2: '67 20 144',
      3: '40 226 198',
      4: '20 144 125',
      5: '84 178 232',
      6: '20 99 144',
      7: '52 29 215',
      8: '141 84 232',
    },
  },
  balancing: {
    base: '14 165 233',
    soft: '224 242 254',
    strong: '7 89 133',
    muted: '56 189 248',
    imbalance: '14 165 233',
    afrrActivation: '56 189 248',
    mfrrActivation: '2 132 199',
    series: {
      1: '28 171 227',
      2: '18 145 117',
      3: '36 27 218',
      4: '110 233 237',
      5: '37 228 136',
      6: '18 77 145',
      7: '83 114 234',
      8: '24 18 145',
    },
  },
  congestion: {
    base: '20 184 166',
    soft: '204 251 241',
    strong: '15 118 110',
    muted: '94 234 212',
    congestion: '13 148 136',
    redispatch: '20 184 166',
    gridLimit: '45 212 191',
    series: {
      1: '38 217 199',
      2: '24 139 98',
      3: '37 94 208',
      4: '24 92 139',
      5: '90 171 226',
      6: '116 213 231',
      7: '37 208 100',
      8: '116 231 161',
    },
  },
  settlement: {
    base: '236 72 153',
    soft: '252 231 243',
    strong: '157 23 77',
    muted: '244 114 182',
    correction: '236 72 153',
    fees: '190 24 93',
    series: {
      1: '217 38 128',
      2: '24 139 66',
      3: '47 218 110',
      4: '230 116 231',
      5: '173 47 218',
      6: '138 24 139',
      7: '139 24 55',
      8: '231 116 147',
    },
  },
  // Generation technologies, resolved from series semantics.technology. `other` is the
  // fallback for a generation series without a technology.
  technology: {
    windOffshore: '21 87 124',
    windOnshore: '125 211 232',
    solar: '245 184 65',
    gas: '239 124 94',
    hardCoal: '139 90 60',
    nuclear: '36 219 158',
    biomass: '101 163 13',
    hydroRunOfRiver: '59 130 246',
    other: '203 213 225',
  },
}

export const marketDark = {
  forward: {
    base: '74 126 238',
    soft: '11 60 126',
    strong: '73 108 224',
    muted: '116 176 251',
    electricity: '98 141 235',
    gas: '182 153 246',
    coal: '238 132 184',
    oil: '34 228 164',
    emissions: '158 160 243',
    series: {
      1: '60 110 226',
      2: '23 94 151',
      3: '33 222 209',
      4: '23 29 151',
      5: '72 33 222',
      6: '159 95 231',
      7: '122 185 235',
      8: '23 151 142',
    },
  },
  dayAhead: {
    base: '19 223 155',
    soft: '18 104 60',
    strong: '11 181 134',
    muted: '69 215 161',
    // TB ramp, dark: same hue per index as light, lightness flipped (TB1 darkest,
    // TB12 lightest) so a longer duration always has more contrast with the
    // surface. Every step >= 4.0:1 on surface-base and raised (both presets).
    tb1: '5 150 105',
    tb2: '5 176 172',
    tb3: '6 184 205',
    tb4: '41 190 239',
    tb5: '84 196 249',
    tb6: '119 201 255',
    tb7: '150 206 255',
    tb8: '175 212 255',
    tb9: '190 218 255',
    tb10: '204 224 254',
    tb11: '217 230 254',
    tb12: '228 237 255',
    series: {
      1: '62 224 170',
      2: '24 128 149',
      3: '36 140 219',
      4: '24 149 70',
      5: '123 215 234',
      6: '29 175 164',
      7: '36 219 103',
      8: '24 95 149',
    },
  },
  intraday: {
    base: '167 131 248',
    soft: '37 14 136',
    strong: '138 83 224',
    muted: '182 159 251',
    ida1: '163 119 238',
    ida2: '182 153 246',
    ida3: '212 199 251',
    idc: '146 98 220',
    series: {
      1: '122 64 221',
      2: '137 125 232',
      3: '125 168 232',
      4: '147 26 131',
      5: '232 125 218',
      6: '217 38 193',
      7: '26 75 147',
      8: '26 43 147',
    },
  },
  reserve: {
    base: '74 126 238',
    soft: '11 60 126',
    strong: '73 108 224',
    muted: '116 176 251',
    fcr: '96 165 250',
    afrr: '122 168 244',
    afrrUp: '122 168 244',
    afrrDown: '56 189 248',
    mfrr: '158 199 249',
    mfrrUp: '158 199 249',
    mfrrDown: '48 82 181',
    series: {
      1: '58 123 228',
      2: '21 38 153',
      3: '31 224 195',
      4: '21 105 153',
      5: '54 31 224',
      6: '147 93 233',
      7: '120 194 237',
      8: '21 153 133',
    },
  },
  balancing: {
    base: '45 181 242',
    soft: '11 83 130',
    strong: '12 147 220',
    muted: '76 196 249',
    imbalance: '71 186 238',
    afrrActivation: '119 207 246',
    mfrrActivation: '17 168 245',
    series: {
      1: '55 181 230',
      2: '19 154 124',
      3: '38 28 227',
      4: '19 82 154',
      5: '28 227 131',
      6: '26 19 154',
      7: '125 119 238',
      8: '119 235 238',
    },
  },
  congestion: {
    base: '24 221 199',
    soft: '15 104 85',
    strong: '25 199 186',
    muted: '112 236 217',
    congestion: '25 198 182',
    redispatch: '42 224 204',
    gridLimit: '102 217 202',
    series: {
      1: '64 221 206',
      2: '26 147 74',
      3: '38 98 217',
      4: '125 232 164',
      5: '38 217 104',
      6: '26 129 147',
      7: '82 166 224',
      8: '26 98 147',
    },
  },
  settlement: {
    base: '240 109 174',
    soft: '123 24 80',
    strong: '225 47 119',
    muted: '245 133 192',
    correction: '238 132 184',
    fees: '222 54 123',
    series: {
      1: '221 64 143',
      2: '147 26 58',
      3: '38 217 104',
      4: '26 147 71',
      5: '169 38 217',
      6: '147 26 147',
      7: '203 125 232',
      8: '125 232 164',
    },
  },
  technology: {
    windOffshore: '56 139 186',
    windOnshore: '123 215 234',
    solar: '247 196 90',
    gas: '244 145 118',
    hardCoal: '176 124 92',
    nuclear: '62 224 170',
    biomass: '132 204 22',
    hydroRunOfRiver: '96 165 250',
    other: '100 116 139',
  },
}
